import { GrowthBookContext } from '@growthbook/growthbook-react';
import type { Session } from '@openx/types/session.ts';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import type { UseAclInfo, UseIsAllowed, UseMatchesOrganizationType, UseSession } from './PermissionHookTypes.ts';
import { type IsGrowthbookFeatureOnFn, useGrowthBookPermissions } from './useGrowthBookPermissions.ts';

export type AppType = 'dsp' | 'oxs' | 'pub' | null;

export type PermissionContextReturn = {
  is(app: AppType): boolean;
  isDsp?: boolean;
  /**
   * Gets value for given feature-flag and if:
   * 1. Value is Boolean - returns this value.
   * 2. Value is String - test against hashed (customStr || email)
   */
  isGrowthbookFeatureOn: IsGrowthbookFeatureOnFn;
  isOa?: boolean;
  isPub?: boolean; // TODO: UIG-540 Temp Workaround for ACL Platform Share
  session?: Session;
  useAclInfo: UseAclInfo;
  useIsAllowed: UseIsAllowed;
  useMatchesOrganizationType?: UseMatchesOrganizationType;
  useSession: UseSession;
};

export type PermissionConfig = {
  session?: Session; // TODO: UIG-540 Temp Workaround for ACL Platform Share
  useAclInfo: UseAclInfo;
  useIsAllowed: UseIsAllowed;
  useMatchesOrganizationType?: UseMatchesOrganizationType;
  useSession: UseSession;
};

export interface PermissionProviderProps {
  growthBookSalt: string;
  appName: AppType;
  userEmail?: string;
  children: ReactNode;
  permissionConfig: PermissionConfig;
  session?: Session; // TODO: UIG-540 Temp Workaround for ACL Platform Share
}

const permissionContext = createContext<PermissionContextReturn>({
  is: () => {
    throw new Error('Not implemented. Ensure you are inside the PermissionContext');
  },
  isGrowthbookFeatureOn: () => {
    throw new Error('Not implemented. Ensure you are inside the PermissionContext');
  },
  isDsp: false,
  isOa: false,
  isPub: false,
  useAclInfo: () => {
    throw new Error('Not implemented. Ensure you are inside the PermissionContext');
  },
  useIsAllowed: () => {
    throw new Error('Not implemented. Ensure you are inside the PermissionContext');
  },
  useMatchesOrganizationType: () => {
    throw new Error('Not implemented. Ensure you are inside the PermissionContext');
  },
  useSession: () => {
    throw new Error('Not implemented. Ensure you are inside the PermissionContext');
  },
});

export const usePermissionContext = () => useContext<PermissionContextReturn>(permissionContext);

export function PermissionProvider({
  children,
  permissionConfig,
  session,
  appName,
  userEmail,
  growthBookSalt,
}: PermissionProviderProps) {
  const { growthbook: growthBook } = useContext(GrowthBookContext);
  const { isGrowthbookFeatureOn } = useGrowthBookPermissions({ userEmail, growthBookSalt, growthBook });

  const is = useCallback(
    (...apps: AppType[]) => {
      return apps.includes(appName);
    },
    [appName]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <appName is intentional>
  const value: PermissionContextReturn = useMemo(
    () => ({
      session,
      ...permissionConfig,
      isDsp: is('dsp'),
      isOa: is('oxs'),
      isPub: is('pub'),
      isGrowthbookFeatureOn: isGrowthbookFeatureOn,
      is,
    }),
    [session, is, isGrowthbookFeatureOn, permissionConfig, appName, growthBook?.ready]
  );

  return <permissionContext.Provider value={value}>{children}</permissionContext.Provider>;
}
